import {AfterViewInit, ChangeDetectorRef, Component, Inject, Input, OnInit, ViewChild} from '@angular/core';
import {MatSort} from '@angular/material/sort';
import {MatPaginator} from '@angular/material/paginator';
import {MatTableDataSource} from '@angular/material/table';
import {SchoolingParticipant, SchoolingParticipantDetails} from '@shared/models/schooling-participant.model';
import {merge} from 'rxjs';
import {filter, tap} from 'rxjs/operators';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MatDialog, MatDialogConfig} from '@angular/material/dialog';
import {TranslationService} from '@shared/services/translation.service';
import {checkPageSize, DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS, pageChange} from '@shared/utils/utils';
import {SchoolingParticipantService} from '@shared/services/schooling-participant.service';
import {ConfirmDialogComponent, ConfirmDialogConfig} from '@shared/components/confirm-dialog/confirm-dialog.component';
import {EmployeeDialogComponent} from '@shared/components/employee-dialog/employee-dialog.component';
import {SchoolingPlanStatusModel} from '@shared/models/schooling-plan.model';
import {SchoolingTuitionDetails} from '@shared/models/schooling-tuition.model';
import {PermissionKeys} from '@shared/models/permission.model';
import {PermissionService} from '@shared/services/permission.service';
import {Store} from '@ngxs/store';

@Component({
    selector: 'saf-schooling-tuition-details-participants',
    templateUrl: './schooling-tuition-details-participants.component.html',
    styleUrls: ['./schooling-tuition-details-participants.component.scss'],
    standalone: false
})
export class SchoolingTuitionDetailsParticipantsComponent implements OnInit, AfterViewInit {
  @Input() public tuitionId: number;
  @Input() public topicId: number;
  @Input() public tuitionStatus: number;
  @Input() public acceptMember: boolean;
  @Input() public tuitionDetails: SchoolingTuitionDetails;
  @Input() public assigneeEmployeeId: number;
  @ViewChild(MatSort, { static: true }) sort: MatSort;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  public tableColumns = ['employeeNumber', 'employeeName', 'status', 'options'];
  public dataSource = new MatTableDataSource<SchoolingParticipantDetails>();
  public totalCount = 0;
  public drawerFilterParams: any = {};
  public searchFilterParams: any = {};
  public loading: boolean = false;
  public hasPermission = false;
  public trackById = (item) => item.id;

  constructor(
    private participantService: SchoolingParticipantService,
    private snackbar: MatSnackBar,
    private dialog: MatDialog,
    private translationService: TranslationService,
    private changeDetectorRef: ChangeDetectorRef,
    private snackBar: MatSnackBar,
    private permissionService: PermissionService,
    private store: Store,
    @Inject(PAGE_SIZE_OPTIONS) public pageSizeOptions,
    @Inject(DEFAULT_PAGE_SIZE) public pageSize,
  ) {
    this.hasPermission = this.permissionService.hasPermission(PermissionKeys.SCHOOLING_TUITIONS);
  }

  ngOnInit(): void {
    this.sort.sortChange.subscribe(() => this.paginator.pageIndex = 0);
    merge(this.sort.sortChange, this.paginator.page).subscribe(() => this.loadTable());
  }

  ngAfterViewInit() {
    this.loadTable();
    this.changeDetectorRef.detectChanges();
  }

  loadTable() {
    const params = {
      filter: {
        ...this.searchFilterParams,
        ...this.drawerFilterParams,
        tuitionId: this.tuitionId
      },
      sort: this.sort.direction ? this.sort.active : '',
      dir: this.sort.direction,
      start: this.paginator.pageIndex * this.paginator.pageSize,
      limit: this.paginator.pageSize,
    };

    this.loading = true;

    this.participantService.getListDetails(params)
      .pipe(tap(() => this.loading = false))
      .subscribe(result => {
        this.totalCount = result.total;
        this.dataSource.data = result.data;
      });
  }


  openDialog() {
    if (!this.hasPermission) {
      return this.returnPermissionError();
    }

    if (this.tuitionDetails.participants.length === this.tuitionDetails.tuition.maxParticipants && this.tuitionDetails.tuition.maxParticipants !== 0) {
      this.snackbar.open(this.translationService.translate('increaseParticipantsInfo'));
    } else {
      const dialogConfig: MatDialogConfig = {
        panelClass: 'table-dialog',
        disableClose: false,
        data: {
          multiple: true,
          showInactive: false,
          showAll: true,
          tuitionTopicId: this.topicId
        }
      };

      this.dialog.open(EmployeeDialogComponent, dialogConfig)
        .afterClosed()
        .pipe(filter((success) => success))
        .subscribe((result) => {
          const params = {
            employeeIds: result,
            tuitionId: this.tuitionId,
            status: 1,
            assigneeEmployeeId: this.assigneeEmployeeId
          };

          this.loading = true;

          if (result) {
            this.participantService.createMultiple(params)
              .pipe(tap(() => this.loading = false))
              .subscribe(() => {
                this.loadTable();
              });
          }
        });
    }

  }

  openDeleteDialog(e, item: SchoolingParticipant) {
    e.stopPropagation();
    if (!this.hasPermission) {
      return this.returnPermissionError();
    }

    if (this.tuitionStatus === 1) {
      this.snackBar.open(this.translationService.translate('notDeletable'));
      return;
    }
    const data = new ConfirmDialogConfig({
      title: this.translationService.translate('confirmDeleteTitle'),
      message: this.translationService.translate('confirmDeleteParticipant'),
      confirmText: this.translationService.translate('confirm'),
    });

    this.dialog.open(ConfirmDialogComponent, {data})
      .afterClosed()
      .pipe(filter((confirmed) => confirmed))
      .subscribe(() => {
        this.loading = true;
        this.participantService.delete(item.id)
          .subscribe((result) => {
            result.success ? this.reloadTable() : this.snackbar.open(this.translationService.translate('notDeletable'));
            this.loading = false;
          });
      });
  }

  reloadTable() {
    SchoolingParticipantService.clearCache();
    this.loadTable();
  }

  applyDrawerFilters(filterParams) {
    this.drawerFilterParams = filterParams;
    this.paginator.pageIndex = 0;
    this.loadTable();
  }
  checkIfStandard(listItem: SchoolingPlanStatusModel) {
    if (listItem.employee) {
      return true;
    }

    return false;
  }

    protected readonly pageChange = pageChange;
  protected readonly checkPageSize = checkPageSize;

  returnPermissionError() {
    return this.snackBar.open(this.translationService.translate('permissionError'));
  }
}
